import type { IntlShape } from 'react-intl';
import { createSelector } from 'reselect';

import type { Filter } from 'components/common/FiltersHeader/types';

import { DEVICES_APP_VERSION, DEVICES_OS_VERSION } from 'constants/FilterConstants';
import { PaginationType } from 'constants/Global';
import { DeviceTypeEnum, DEVICES_TYPE_SHOWN, OTHERS } from 'modules/devices/constants';
import type { DeviceOSVersionBucket } from 'modules/devices/types';
import { formatFilters } from 'modules/devices/utils';
import { getSelectedGroupId } from 'modules/groups/selectors';
import { isGroupManagement } from 'modules/router/selectors';
import { getUserByIds } from 'modules/users/selectors';
import { getSelectedDealerId } from 'selectors/dealerSelectors';
import { sortByVersion } from 'utils/global';
import { getProps, getRoot } from 'utils/selectors';

import { getState, getUI } from './index';

const getDashboard = createSelector(getUI, ui => ui.dashboard);

const getAll = createSelector(getState, state => state.data.entities);

const getActions = createSelector(getDashboard, dashboard => dashboard.actions);

export const getFilters = createSelector(getDashboard, dashboard => dashboard.filters);

export const getDashboardItems = createSelector(getDashboard, dashboard => dashboard.deviceIds);

export const getDevicesContent = createSelector(getDashboardItems, getAll, (items, devices) =>
  items.map(id => devices?.[id]));

export const getDevicesContentPage = createSelector(getDashboard, dashboard => dashboard.page);

export const getCreatedDevice = createSelector(getDashboard, dashboard => dashboard.createdDevice);

export const hasNext = createSelector(getDashboard, dashboard => dashboard.hasNext);

export const getDeviceApiError = createSelector(getActions, actions => actions.apiError);

export const getDevicesIsFetching = createSelector(getActions, actions => actions.isFetching);

export const getDeviceIsCreating = createSelector(getActions, actions => actions.isCreating);

export const getDevicesIsDeleting = createSelector(getActions, actions => actions.isDeleting);

export const getCreationDeviceApiError = createSelector(getActions, actions => actions.creationDeviceApiError);

export const isCheckingDeviceName = createSelector(getActions, actions => actions.deviceName.loading);

export const getDeviceNameCheckError = createSelector(getActions, actions => actions.deviceName.error);

export const getDeviceSearchText = createSelector(getFilters, filters => filters.searchText);

export const getSelectedDevicesApp = createSelector(getFilters, filters => filters.appVersion);

export const getSelectedDevicesOS = createSelector(getFilters, filters => filters.osVersion);

const getAppVersions = createSelector(getDashboard, dashboard => dashboard.appVersions);

const getOSVersions = createSelector(getDashboard, dashboard => dashboard.osVersions);

export const isFetchingAppVersions = createSelector(getAppVersions, ({ loading }) => loading);

export const getFormattedAppVersions = createSelector(getAppVersions, ({ data }) => {
  const versions = Object.values(data);
  versions.sort(sortByVersion('lastAppVersion')).reverse();
  return versions;
});

export const isFetchingOSVersions = createSelector(getOSVersions, ({ loading }) => loading);

export const getFormattedOSVersions = createSelector(getOSVersions, ({ data }) =>
  Object.values(data).reduce<Partial<Record<DeviceTypeEnum, DeviceOSVersionBucket[]>>>((acc, version) => {
    const byType = [...(acc[version.type] ?? []), version];
    byType.sort(sortByVersion('operatingSystemVersion')).reverse();
    return {
      ...acc,
      [version.type]: byType,
    };
  }, {}));

const getSelectedUserIds = createSelector(getFilters, filters => filters.userId);

const getSelectedUsers = createSelector(getRoot, getSelectedUserIds, getUserByIds);

const getDeviceTypesParams = createSelector(getFilters, ({ deviceTypes = [] }) => {
  if (deviceTypes.length === 0) {
    return DEVICES_TYPE_SHOWN;
  }
  return deviceTypes.reduce<string[]>(
    (acc, type) => [...acc, ...(type !== OTHERS ? [type] : [DeviceTypeEnum.TVOS, DeviceTypeEnum.UNKNOWN])],
    []
  );
});

const prepareRequestParams = createSelector(getFilters, ({ searchText: text, appVersion, osVersion, ...others }) => {
  const filters = formatFilters({}, DEVICES_APP_VERSION, appVersion);
  return {
    ...formatFilters(filters, DEVICES_OS_VERSION, osVersion),
    text,
    ...others,
  };
});

export const getRequestParameters = createSelector(
  prepareRequestParams,
  getSelectedDealerId,
  getSelectedGroupId,
  getDeviceTypesParams,
  getDevicesContentPage,
  isGroupManagement,
  (filters, dealerId, groupId, deviceTypes, page, isGroupPage) => ({
    ...filters,
    ...(isGroupPage ? { groupId, notExist: 'dealerId' } : { dealerId }),
    deviceTypes,
    paginationType: PaginationType.SLICE,
    page: page.number,
    size: page.size,
    sort: 'name',
  })
);

export const getAppVersionRequestParameters = createSelector(getSelectedDealerId, dealerId => ({
  dealerId,
}));

export const getOSVersionRequestParameters = createSelector(getSelectedDealerId, dealerId => ({
  dealerId,
}));

const getFormattedApp = createSelector(
  getProps<{ intl: IntlShape }>,
  getSelectedDevicesApp,
  ({ intl }, app): false | Filter =>
    app.length > 0 && {
      id: 'appVersion',
      name: intl.formatMessage({ id: 'page.device.filters.appVersionShort', defaultMessage: 'App version' }),
      values: app.map(version => ({
        value: version,
        name: version || intl.formatMessage({ id: 'others', defaultMessage: 'Others' }),
      })),
    }
);

const getFormattedOS = createSelector(
  getProps<{ intl: IntlShape }>,
  getSelectedDevicesOS,
  ({ intl }, OS): false | Filter =>
    OS.length > 0 && {
      id: 'osVersion',
      name: intl.formatMessage({ id: 'page.device.filters.system', defaultMessage: 'OS version' }),
      values: OS.map(version => ({
        value: version,
        name: version || intl.formatMessage({ id: 'others', defaultMessage: 'Others' }),
      })),
    }
);

const getFormattedDeviceTypes = createSelector(
  getProps<{ intl: IntlShape }>,
  getFilters,
  ({ intl }, { deviceTypes }): false | Filter =>
    deviceTypes.length > 0 && {
      id: 'deviceTypes',
      name: intl.formatMessage({ id: 'page.device.filters.deviceTypes.title', defaultMessage: 'Devices type' }),
      values: deviceTypes.map(type => ({
        value: type,
        name: intl.formatMessage({ id: `page.device.filters.deviceTypes.${type}`, defaultMessage: type }),
      })),
    }
);

const getFormattedUsers = createSelector(
  getProps<{ intl: IntlShape }>,
  getSelectedUsers,
  ({ intl }, users): false | Filter =>
    users.length > 0 && {
      id: 'userId',
      name: intl.formatMessage({ id: 'users', defaultMessage: 'Users' }),
      values: users.map((user: { id: string; displayName?: string }) => ({
        value: user.id,
        name: user.displayName,
      })),
    }
);

export const getFormattedFilters = createSelector(
  getFormattedApp,
  getFormattedOS,
  getFormattedDeviceTypes,
  getFormattedUsers,
  (...args) =>
    args.reduce<Filter[]>((acc, items) => {
      if (items) {
        acc.push(items);
      }
      return acc;
    }, [])
);

export const isCreateModalOpen = createSelector(getDashboard, ({ creationModal }) => creationModal);

const sendByEmailState = createSelector(getDashboard, state => state.sendByEmail);

export const isSending = createSelector(sendByEmailState, state => state.isSending);

export const getEmail = createSelector(sendByEmailState, state => state.email);

export const canSendQRCodeByEmail = createSelector(
  isSending,
  getEmail,
  (isPosting, email) => (!isPosting && !email) || (isPosting && Boolean(email))
);
